import React from 'react';
import { Box, Chip, LinearProgress, Paper, Typography } from '@mui/material';
import type { LinearProgressProps } from '@mui/material/LinearProgress';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TranslationKey } from '../../types/i18n';

type TFn = (key: TranslationKey, options?: unknown) => string;

export type UsageCallChatMeterPapersProps = {
  usagePaperSx: SxProps<Theme>;
  loading: boolean;
  dataUnavailable: boolean;
  hasRealData: boolean;
  hasMinuteCap: boolean;
  hasMessageCap: boolean;
  minutesOverage: number;
  messagesOverage: number;
  minutesPercent: number;
  messagesPercent: number;
  minutesBarColor: LinearProgressProps['color'];
  messagesBarColor: LinearProgressProps['color'];
  minutesUsageLabel: string | null;
  messagesUsageLabel: string | null;
  t: TFn;
};

/**
 * Call + chat usage meter cards (hidden when bundle_credit_pool shows the combined balance).
 */
const UsageCallChatMeterPapers: React.FC<UsageCallChatMeterPapersProps> = ({
  usagePaperSx,
  loading,
  dataUnavailable,
  hasRealData,
  hasMinuteCap,
  hasMessageCap,
  minutesOverage,
  messagesOverage,
  minutesPercent,
  messagesPercent,
  minutesBarColor,
  messagesBarColor,
  minutesUsageLabel,
  messagesUsageLabel,
  t,
}) => (
  <>
    <Paper sx={{ ...usagePaperSx, order: 1 }}>
      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
        {t('Call minutes')}
      </Typography>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <LinearProgress
            variant="indeterminate"
            sx={{
              flex: 1,
              minWidth: 160,
              height: 10,
              borderRadius: 1,
            }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={t('Loading...')}
            sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
          />
        </Box>
      ) : (
        <Box>
          {minutesOverage > 0 && (
            <Typography
              variant="body2"
              fontWeight={600}
              color="error.main"
              sx={{ mb: 1 }}
            >
              {t('Overage minutes this billing period', {
                extra: minutesOverage,
              })}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <LinearProgress
              variant="determinate"
              value={dataUnavailable ? 0 : hasMinuteCap ? minutesPercent : 0}
              sx={{
                flex: 1,
                minWidth: 160,
                height: 10,
                borderRadius: 1,
                ...(dataUnavailable || (!hasMinuteCap && hasRealData)
                  ? { opacity: dataUnavailable ? 0.38 : 0.45 }
                  : {}),
              }}
              color={dataUnavailable ? 'primary' : minutesBarColor}
            />
            <Chip
              size="small"
              variant="outlined"
              label={minutesUsageLabel ?? '—'}
              sx={{
                fontVariantNumeric: 'tabular-nums',
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>
      )}
    </Paper>
    <Paper sx={{ ...usagePaperSx, order: 2 }}>
      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
        {t('Chat messages')}
      </Typography>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <LinearProgress
            variant="indeterminate"
            sx={{
              flex: 1,
              minWidth: 160,
              height: 10,
              borderRadius: 1,
            }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={t('Loading...')}
            sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
          />
        </Box>
      ) : (
        <Box>
          {messagesOverage > 0 && (
            <Typography
              variant="body2"
              fontWeight={600}
              color="error.main"
              sx={{ mb: 1 }}
            >
              {t('Overage messages this billing period', {
                extra: messagesOverage,
              })}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <LinearProgress
              variant="determinate"
              value={dataUnavailable ? 0 : hasMessageCap ? messagesPercent : 0}
              sx={{
                flex: 1,
                minWidth: 160,
                height: 10,
                borderRadius: 1,
                ...(dataUnavailable || (!hasMessageCap && hasRealData)
                  ? { opacity: dataUnavailable ? 0.38 : 0.45 }
                  : {}),
              }}
              color={dataUnavailable ? 'primary' : messagesBarColor}
            />
            <Chip
              size="small"
              variant="outlined"
              label={messagesUsageLabel ?? '—'}
              sx={{
                fontVariantNumeric: 'tabular-nums',
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>
      )}
    </Paper>
  </>
);

export default UsageCallChatMeterPapers;
